// Mineral Intelligence — AOI API client wrappers.
// Mirrors the schemas exported by functions/api/mi/aois/*.

export interface AoiListItem {
  id: string;
  name: string;
  /** Optional operator display label; when set, shown in place of name. */
  nickname: string | null;
  county: string | null;
  basin: string | null;
  pinned_at: string;
  last_activity_at: string | null;
  party_count: number;
  doc_count: number;
  last_recording_date: string | null;
  trs_keys: string[];
}

export interface AoiTrs {
  township_num: number;
  township_dir: 'N' | 'S';
  range_num: number;
  range_dir: 'E' | 'W';
  section: number;
}

export interface AoiMeta {
  id: string;
  name: string;
  nickname: string | null;
  county: string | null;
  basin: string | null;
  pinned_at: string;
  last_activity_at: string | null;
  claude_weekly_read: string | null;
  has_map_image: boolean;
  map_image_uploaded_at: string | null;
  notes: string | null;
  notes_updated_at: string | null;
  trs: AoiTrs[];
}

export interface RosterRow {
  party_id: string;
  display_name: string;               // surface form (LASTNAME FIRST, all caps)
  display_name_natural: string | null; // human form (First Last); fall back to display_name when null
  normalized_name: string;
  entity_type: string;
  holdings_in_aoi: number;
  last_recording_date: string | null;
  last_lease_effective_date: string | null;
  has_address: boolean;
  has_phone: boolean;
  doc_numbers: string[] | null;       // this party's doc_numbers inside the AOI, recent first
  trs_keys: string[] | null;          // TRS tuples this party holds inside the AOI (skip-trace payload)
  last_pushed_to_close_at: string | null;
  skipped_at: string | null;
  /** Non-terminal lead-list status: 'researched' (skip-trace done) > 'pending' > null. */
  lead_list_status: 'pending' | 'researched' | null;
  /** True when this party appears as grantor on a mineral or royalty deed
   *  whose TRS overlaps the AOI — i.e., they conveyed their interest. */
  has_sold_minerals: boolean;
}

export interface AoiRosterDoc {
  doc_number: string;
  ordinal: number;
  doc_type: string | null;
  recording_date: string | null;
  effective_date: string | null;
  primary_term_months: number | null;
  royalty_pct: string | null;
  gross_acreage: string | null;
  lessee: string | null;
  trs_label: string | null;
}

export interface AoiActivePoolOrder {
  pool_order_id: string;
  docket_number: string | null;
  cause_number: string | null;
  application_type: string | null;
  operator: string | null;
  parse_status: string;
  filed_date: string | null;
  hearing_date: string | null;
  names_on_order: number;
  names_matched_to_aoi: number;
}

export interface AoiDetailResponse {
  aoi: AoiMeta;
  roster: RosterRow[];
  counts: { all: number; lapsed: number; active: number; aggregators: number; multi_tract: number };
  active_pool_orders: AoiActivePoolOrder[];
}

export interface PinAoiInput {
  name?: string;
  basin?: string | null;
  trs: Array<{
    township_num: number;
    township_dir: 'N' | 'S';
    range_num: number;
    range_dir: 'E' | 'W';
    section: string | number;
  }>;
}

// Shared auth-header logic lives in src/lib/authHeaders.ts — see the
// header comment there for the .pages.dev / Vite-dev fallback rationale.
import { authHeaders } from '../../lib/authHeaders';

async function http<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json() as Promise<T>;
}

export const aoiApi = {
  list: () =>
    http<{ aois: AoiListItem[] }>('/api/mi/aois'),
  get: (id: string) =>
    http<AoiDetailResponse>(`/api/mi/aois/${id}`),
  pin: (body: PinAoiInput) =>
    http<{ id: string; name: string; basin: string | null; trs_count: number }>(
      '/api/mi/aois',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),
  unpin: (id: string) =>
    http<{ ok: true }>(`/api/mi/aois/${id}`, { method: 'DELETE' }),
  uploadMapImage: async (id: string, blob: Blob): Promise<{ ok: true; bytes: number }> => {
    const r = await fetch(`/api/mi/aois/${id}/map-image`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders(), 'Content-Type': blob.type || 'image/png' },
      body: blob,
    });
    if (!r.ok) {
      let detail = r.statusText;
      try { detail = (await r.json()).error ?? detail; } catch {}
      throw new Error(`${r.status} ${detail}`);
    }
    return r.json();
  },
  deleteMapImage: (id: string) =>
    http<{ ok: true }>(`/api/mi/aois/${id}/map-image`, { method: 'DELETE' }),
  partyDocs: (aoiId: string, partyId: string) =>
    http<{ documents: AoiRosterDoc[] }>(`/api/mi/aois/${aoiId}/parties/${partyId}/docs`),
  patchNotes: (id: string, notes: string | null) =>
    http<{ ok: true; notes: string | null; notes_updated_at: string | null }>(
      `/api/mi/aois/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes }) }
    ),
  /** Set (or clear, with empty string) the AOI's display nickname. */
  patchNickname: (id: string, nickname: string | null) =>
    http<{ ok: true; nickname: string | null }>(
      `/api/mi/aois/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nickname }) }
    ),
  /** Set (or clear) the AOI's county (skip-trace owner payload). */
  patchCounty: (id: string, county: string | null) =>
    http<{ ok: true; county: string | null }>(
      `/api/mi/aois/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ county }) }
    ),
  exportDocNumbers: async (id: string): Promise<string> => {
    const r = await fetch(`/api/mi/aois/${id}/export-doc-numbers`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.text();
  },
};
